//Built-in libraries

import { InformationCircleIcon, PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
//Third party libraries
import { v4 as uuidv4 } from 'uuid';
//Custom components
import Button from '@/components/UI/Button';
import Confirm from '@/components/UI/Modal/Confirm';
//Services
import { useApplicationFormContext } from '@/features/ApplicationForm/ApplicationFormProvider';
import { createInputChangedHandler } from '@/features/ApplicationForm/utils/formHandlers';
import { isObjectEmpty } from '@/utils';
import AddHouseholdIncomeForm from './AddHouseholdIncomeForm';
import { initialValues } from './constants';
import type { IHouseholdIncome } from './types';

//STP Libraries

//Assets and Miscellaneous

//Component Begin

export default function DevelopmentTeam() {
  const { setCurrentStep, updateData, data } = useApplicationFormContext();
  const [showRemoveConfirm, setShowRemoveConfirm] = useState<boolean>(false);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [householdIncome, setHouseholdIncome] = useState(initialValues);
  const [selectedTeam, setSelectedTeam] = useState<IHouseholdIncome>();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editTeamId, setEditTeamId] = useState<string>('');

  const removeTeamHandler = (team: IHouseholdIncome) => {
    setShowRemoveConfirm(true);
    setSelectedTeam(team);
  };

  const confirmRemoveIncome = async () => {
    setShowRemoveConfirm(false);
    const filteredTeams = data.householdIncome.filter((t: any) => t.id !== selectedTeam?.id);
    updateData({
      householdIncome: filteredTeams,
    });
  };

  const cancelRemoveIncome = () => {
    setShowRemoveConfirm(false);
  };

  const toggleAddModal = () => {
    resetForm();
    setShowAddModal((prev) => !prev);
  };

  const resetForm = () => {
    setHouseholdIncome(initialValues);
    setEditTeamId('');
  };

  const inputChangedHandler = createInputChangedHandler(setHouseholdIncome, householdIncome);

  const validateForm = () => {
    const formData = { ...householdIncome };

    const formDataErrors = Object.keys(formData).reduce((prev: { [key: string]: string }, next: string) => {
      if (formData[next].error.length >= 1) {
        return {
          ...prev,
          [next]: formData[next].error,
        };
      }
      if (!formData[next].isTouched && formData[next].rules.required) {
        setHouseholdIncome((prevState) => ({
          ...prevState,
          [next]: { ...prevState[next], error: 'Required' },
        }));
        return {
          ...prev,
          [next]: 'Required',
        };
      }
      return { ...prev };
    }, {});

    return isObjectEmpty(formDataErrors);
  };

  const onAddIncomeHandler = () => {
    if (validateForm()) {
      const formattedData = Object.keys(householdIncome).reduce(
        (
          prev: {
            [key: string]: string | number | boolean | Object | undefined;
          },
          next: string
        ) => {
          return { ...prev, [next]: householdIncome[next].value };
        },
        {}
      );

      updateData({
        householdIncome: [...data.householdIncome, { ...formattedData, id: uuidv4() }],
      });
      resetForm();
      toggleAddModal();
    }
  };

  const editTeamHandler = (team: any) => {
    toggleAddModal();
    setIsEditing(true);
    const formData = Object.keys(householdIncome).reduce((prev, next) => {
      return {
        ...prev,
        [next]: {
          ...householdIncome[next],
          value: team[next],
          isTouched: !!team[next],
        },
      };
    }, {});
    setHouseholdIncome(formData);
    setEditTeamId(team.id);
  };

  const updateIncomeHandler = () => {
    toggleAddModal();
    if (validateForm()) {
      const formattedData = Object.keys(householdIncome).reduce(
        (
          prev: {
            [key: string]: string | number | boolean | Object | undefined;
          },
          next: string
        ) => {
          return {
            ...prev,
            [next]: householdIncome[next].value,
            id: editTeamId,
          };
        },
        {}
      );
      updateData({
        householdIncome: data.householdIncome.map((t: any) => {
          if (t.id === editTeamId) {
            return formattedData;
          }
          return t;
        }),
      });
      resetForm();
      setIsEditing(false);
    }
  };

  return (
    <div className='space-y-6 p-6 lg:col-span-8 rounded-lg'>
      <h3 className='text-lg leading-6 font-medium text-primaryColor-normal'>Household Income</h3>
      <div className='flex flex-row justify-between'>
        <p className='block text-sm text-neutralColor-dark'>Total Household Income of the Family</p>
        <Button label='Add' type='button' btnStyle='primary' onClick={toggleAddModal} icon={<PlusIcon />} />
      </div>
      {showRemoveConfirm && (
        <Confirm
          title='Remove Development Team'
          message={'You are about to remove household income. Are you sure you want to remove ?'}
          acceptButtonLabel='Remove'
          declineButtonLabel='Cancel'
          accept={confirmRemoveIncome}
          decline={cancelRemoveIncome}
        />
      )}
      {showAddModal && (
        <AddHouseholdIncomeForm
          isEditing={isEditing}
          income={householdIncome}
          modalHandler={toggleAddModal}
          inputChangedHandler={inputChangedHandler}
          onAddIncomeHandler={onAddIncomeHandler}
          updateIncomeHandler={updateIncomeHandler}
        />
      )}
      {data.householdIncome.length === 0 && (
        <div className='p-2 mt-2 flex flex-col items-center justify-center gap-2 h-screen/2'>
          <span>
            <InformationCircleIcon className='h-8 w-8 text-tertiaryColor-normal' />
          </span>
          <p>You have not added any family members</p>
        </div>
      )}
      {data?.householdIncome && data?.householdIncome.length > 0 && (
        <div className='space-y-2 w-full h-full ring-1 ring-neutralColor-lighter rounded-xl overflow-hidden'>
          <table className='divide-y divide-gray-200 w-full'>
            <thead className='bg-gray-50'>
              <tr>
                <th className='p-4 text-left text-xs font-bold text-gray-500 capitalize tracking-wider'>Family Member Name</th>
                <th className='p-4 text-left text-xs font-bold text-gray-500 capitalize tracking-wider'>Age</th>
                <th className='p-4 text-left text-xs font-bold text-gray-500 capitalize tracking-wider'>Relation</th>
                <th className='p-4 text-left text-xs font-bold text-gray-500 capitalize tracking-wider'>Income</th>
                <th className='p-4 text-left text-xs font-bold text-gray-500 capitalize tracking-wider'></th>
              </tr>
            </thead>
            <tbody className='bg-white divide-y divide-gray-200 rounded-lg'>
              {data?.householdIncome?.map((income: IHouseholdIncome) => (
                <tr
                  key={income.id}
                  className={`${income.id === editTeamId ? 'bg-neutralColor-lighter' : 'cursor-pointer hover:bg-neutralColor-lighter'}`}
                >
                  <td className='p-4 text-gray-400'>{income.familyMemberName}</td>
                  <td className='p-4 text-gray-400'>{income.age}</td>
                  <td className='p-4 text-gray-400'>{income.relation}</td>
                  <td className='p-4'>
                    <div className='text-gray-400'>Salary: ${income?.wages || 0}</div>
                    <div className='text-gray-400'>Benefits: ${income?.pensions || 0}</div>
                    <div className='text-gray-400'>Others: ${income?.otherIncome || 0}</div>
                    <div className='text-gray-400'>Public Assistance: ${income?.publicAssistance || 0}</div>
                  </td>
                  <td className='p-4 flex gap-4'>
                    <div className=' cursor-pointer' onClick={() => editTeamHandler(income)}>
                      <PencilIcon className='h-5 w-5 text-neutralColor-normal hover:text-blue-500' />
                    </div>
                    <div className='cursor-pointer' onClick={() => removeTeamHandler(income)}>
                      <TrashIcon className='h-5 w-5 text-neutralColor-normal hover:text-tertiaryColor-normal' />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className='flex gap-4 justify-end'>
        <Button label='Previous' btnStyle='neutral' type='button' onClick={() => setCurrentStep((prev) => prev - 1)} />
        <Button label='Next' btnStyle='primary' type='button' onClick={() => setCurrentStep((prev) => prev + 1)} />
      </div>
    </div>
  );
}
